// src/security/v2/evaluator.ts
//
// Permissions v2 evaluator (ADR-001 / DEV-776).
// Pure function: given a user, a project, and the preloaded V2 data,
// returns the effective capability set. No fetch, no side effects.
//
// Rules, in order:
//   1. System admin fast-path — returns every assignable capability.
//   2. Slot-based grants — union of bundles from permission_grants matching
//      any role_slot the user actively occupies, scoped to this project.
//   3. User-direct grants — union of bundles from permission_grants where
//      user_guid matches, scoped to this project.
//   4. Collection cascade — for each collection containing this project,
//      if the user has collection:view (user-direct at collection scope),
//      union CASCADABLE_SET.
//   5. Public-visibility default — if the project is public, union
//      CASCADABLE_SET for any authenticated user.
//
// Union semantics only. No subtractive overrides in v1 (keeps the audit
// trail explainable per ADR-001 §"Bundle → subject binding").

import type {
  CapabilityName,
  DebugRuleTrace,
  EvaluationInputs,
  EvaluationResult,
  GUID,
  PermissionGrantDto,
  PermissionSetDto,
} from "./types";
import { CASCADABLE_SET } from "./types";

export interface EvaluateOptions {
  debug?: boolean;
}

// ── GUID normalization ───────────────────────────────────────
// DEV-1014. The evaluator was using strict `===` on GUID strings,
// which silently misfired whenever caller-supplied GUIDs (often
// uppercased for display or lookup) compared against grant GUIDs
// returned by the API (lowercase by PostgreSQL default). Per CLAUDE.md:
// "GUID comparisons: Always case-insensitive."
//
// `normGuid` is also used to normalize Map keys built from
// permissionSetGuid so `Map.get(...)` lookups are case-insensitive too.
// (Map equality is strict by definition; normalizing both sides at
// insert + get sidesteps the limitation.)
const normGuid = (g: GUID | null | undefined): string =>
  String(g ?? "").toUpperCase();

const eqGuid = (
  a: GUID | null | undefined,
  b: GUID | null | undefined,
): boolean => normGuid(a) === normGuid(b);

export function evaluateSecurityForUserV2(
  projectGuid: GUID,
  inputs: EvaluationInputs,
  opts: EvaluateOptions = {}
): EvaluationResult {
  const debug: DebugRuleTrace[] | undefined = opts.debug ? [] : undefined;
  const caps = new Set<CapabilityName>();

  // ── Rule 1: System admin fast-path ────────────────────────────
  if (inputs.isSystemAdmin) {
    for (const c of inputs.capabilities) {
      if (c.isAssignable) caps.add(c.name);
    }
    debug?.push({
      rule: "system_admin_fastpath",
      matched: true,
      capsAdded: [...caps],
      note: "Short-circuit: system admin grants every assignable capability.",
    });
    return { userGuid: inputs.userGuid, projectGuid, capabilities: caps, debug };
  }

  // Precompute: bundle_guid → Set<capability_name>.
  const bundleCaps = indexBundleCapabilities(inputs.permissionSets);
  const now = Date.now();

  // DEV-892: skip non-active grants. Pending grants are dormant until the
  // recipient accepts via Action Center; declined/revoked are cleanup-only.
  // `undefined` status preserves backward compat with old payloads.
  const isStatusActive = (g: PermissionGrantDto): boolean =>
    g.status === undefined || g.status === "active";

  const isActiveProjectGrant = (g: PermissionGrantDto): boolean =>
    g.scopeType === "project" &&
    eqGuid(g.scopeGuid, projectGuid) &&
    (g.expiresAt == null || Date.parse(g.expiresAt) > now) &&
    isStatusActive(g);

  // ── Rule 2: Slot-based grants on this project ─────────────────
  const slotGrants = inputs.myGrants.slotGrants.filter(isActiveProjectGrant);
  const rule2Caps = unionBundleCaps(slotGrants, bundleCaps);
  addAll(caps, rule2Caps);
  debug?.push({
    rule: "slot_based_project_grants",
    matched: slotGrants.length > 0,
    capsAdded: [...rule2Caps],
    note: `Matched ${slotGrants.length} slot grant(s) on this project.`,
  });

  // ── Rule 3: User-direct grants on this project ────────────────
  const directGrants = inputs.myGrants.directGrants.filter(isActiveProjectGrant);
  const rule3Caps = unionBundleCaps(directGrants, bundleCaps);
  addAll(caps, rule3Caps);
  debug?.push({
    rule: "user_direct_project_grants",
    matched: directGrants.length > 0,
    capsAdded: [...rule3Caps],
    note: `Matched ${directGrants.length} direct grant(s) on this project.`,
  });

  // ── Rule 4: Collection cascade ────────────────────────────────
  // A user with collection:view on any collection containing this project
  // inherits the cascadable view capabilities. collection:view is always a
  // user-direct grant in v1 (slot-based grants are not meaningful at
  // collection scope — enforced by migration 116 CHECK).
  const cascadingCollections: GUID[] = [];
  for (const cGuid of inputs.collectionGuidsForProject) {
    const hasView = inputs.myGrants.directGrants.some(
      (g) =>
        g.scopeType === "collection" &&
        eqGuid(g.scopeGuid, cGuid) &&
        (g.expiresAt == null || Date.parse(g.expiresAt) > now) &&
        bundleCaps.get(normGuid(g.permissionSetGuid))?.has("collection:view")
    );
    if (hasView) cascadingCollections.push(cGuid);
  }
  if (cascadingCollections.length > 0) {
    const rule4Caps = new Set<CapabilityName>(CASCADABLE_SET);
    addAll(caps, rule4Caps);
    debug?.push({
      rule: "collection_cascade",
      matched: true,
      capsAdded: [...rule4Caps],
      note: `Cascaded from ${cascadingCollections.length} collection(s): ${cascadingCollections.join(", ")}.`,
    });
  } else {
    debug?.push({
      rule: "collection_cascade",
      matched: false,
      capsAdded: [],
      note: `No collection:view grant on the ${inputs.collectionGuidsForProject.length} collection(s) containing this project.`,
    });
  }

  // ── Rule 5: Public-visibility default ─────────────────────────
  if (inputs.projectVisibility === "public") {
    const rule5Caps = new Set<CapabilityName>(CASCADABLE_SET);
    addAll(caps, rule5Caps);
    debug?.push({
      rule: "public_visibility_default",
      matched: true,
      capsAdded: [...rule5Caps],
      note: "Project visibility is public — cascadable view set granted to every authenticated user.",
    });
  } else {
    debug?.push({
      rule: "public_visibility_default",
      matched: false,
      capsAdded: [],
      note: "Project visibility is private.",
    });
  }

  return { userGuid: inputs.userGuid, projectGuid, capabilities: caps, debug };
}

// ── Community-scope evaluator (DEV-859 [D2] / DEV-854) ────────
//
// Parallel to evaluateSecurityForUserV2 but for community-scope
// capability evaluation. Different rule set:
//
//   1.  System admin fast-path — same as project rule 1.
//   1.5 Scoped sysadmin (Org Admin marker, DEV-859). User holds a
//       grant of an `isScopedSysadmin = true` bundle at the target
//       community OR any of its ancestors → return all assignable
//       community-scope capabilities. Self-and-ancestor walk is
//       short-circuited the moment a match is found.
//   2.  User-direct grants at scope_type='community', scope_guid=target
//       — direct grants on this community only.
//   6.  Operational cascade (DEV-854 Decision 3, option iii). Walk
//       the ancestor chain. For each grant whose bundle includes a
//       capability ending in `_descendants`, union the corresponding
//       non-`_descendants` capability into the result for the target.
//       Per the option-iii lock, this is assignment-cascading only —
//       the cap mapping is mechanical: 'community:admin_descendants'
//       confers 'community:admin' at the target.
//
// Plus the requires_2fa gate (DEV-871): any rule contributing caps
// from a bundle with `requires2fa = true` is skipped if the user
// doesn't have 2FA enrolled (`is2FAEnabled = false`). The grant row
// stays in the database — it's dormant until 2FA enrollment.
//
// Slot-based grants (Rule 2 of the project evaluator) are not
// processed here — slot-based grants are project-scope only in v1
// per the migration 116 CHECK constraint.
//
// Project-scope and collection-scope caps are NOT returned by this
// evaluator even if a community grant somehow includes one. Per
// DEV-854 Decision 2, community-level cascade is "right to assign"
// only — direct authority on projects in the community's tree must
// flow through project-scope grants.

export function evaluateCommunitySecurityV2(
  communityGuid: GUID,
  inputs: import("./types").CommunityEvaluationInputs,
  opts: EvaluateOptions = {}
): import("./types").CommunityEvaluationResult {
  const debug: DebugRuleTrace[] | undefined = opts.debug ? [] : undefined;
  const caps = new Set<CapabilityName>();

  // ── Rule 1: System admin fast-path ────────────────────────────
  if (inputs.isSystemAdmin) {
    for (const c of inputs.capabilities) {
      if (c.isAssignable) caps.add(c.name);
    }
    debug?.push({
      rule: "system_admin_fastpath",
      matched: true,
      capsAdded: [...caps],
      note: "Short-circuit: system admin grants every assignable capability.",
    });
    return { userGuid: inputs.userGuid, communityGuid, capabilities: caps, debug };
  }

  // Precompute lookups.
  const bundleCaps = indexBundleCapabilities(inputs.permissionSets);
  const bundleByGuid = new Map<GUID, PermissionSetDto>();
  for (const s of inputs.permissionSets) bundleByGuid.set(normGuid(s.permissionSetGuid), s);
  const now = Date.now();

  // Helper: is this grant blocked by the requires_2fa gate?
  const isGrantBlockedBy2FA = (grantPermissionSetGuid: GUID): boolean => {
    if (inputs.is2FAEnabled) return false;
    const bundle = bundleByGuid.get(normGuid(grantPermissionSetGuid));
    return !!bundle?.requires2fa;
  };

  // DEV-892: skip non-active grants in community evaluator too.
  const isStatusActive = (g: PermissionGrantDto): boolean =>
    g.status === undefined || g.status === "active";

  const isActiveCommunityGrant = (g: PermissionGrantDto, scopeGuid: GUID): boolean =>
    g.scopeType === "community" &&
    eqGuid(g.scopeGuid, scopeGuid) &&
    (g.expiresAt == null || Date.parse(g.expiresAt) > now) &&
    isStatusActive(g);

  // ── Rule 1.5: Scoped sysadmin (Org Admin marker) ─────────────
  // Walk self + ancestors. First match wins; return all assignable
  // community-scope caps and short-circuit.
  const selfAndAncestors: GUID[] = [communityGuid, ...inputs.ancestorCommunityGuidsForCommunity];
  let scopedSysadminAtScope: GUID | null = null;
  for (const scope of selfAndAncestors) {
    const found = inputs.myGrants.directGrants.some((g) =>
      isActiveCommunityGrant(g, scope) &&
      !isGrantBlockedBy2FA(g.permissionSetGuid) &&
      bundleByGuid.get(normGuid(g.permissionSetGuid))?.isScopedSysadmin === true
    );
    if (found) { scopedSysadminAtScope = scope; break; }
  }

  if (scopedSysadminAtScope) {
    for (const c of inputs.capabilities) {
      if (c.isAssignable && c.scopeType === "community") {
        caps.add(c.name);
      }
    }
    debug?.push({
      rule: "rule_1_5_scoped_sysadmin",
      matched: true,
      capsAdded: [...caps],
      note: `Scoped-sysadmin bundle held at community scope ${scopedSysadminAtScope}; granting all assignable community-scope caps on target ${communityGuid}.`,
    });
    return { userGuid: inputs.userGuid, communityGuid, capabilities: caps, debug };
  }

  // ── Rule 2: User-direct grants at the target community scope ─
  const directAtTarget = inputs.myGrants.directGrants.filter((g) =>
    isActiveCommunityGrant(g, communityGuid) &&
    !isGrantBlockedBy2FA(g.permissionSetGuid)
  );
  const rule2Caps = unionBundleCaps(directAtTarget, bundleCaps);
  // Filter to community-scope caps only — defensive against bundles that
  // might include caps from other scope types.
  const communityCapNames = new Set<CapabilityName>(
    inputs.capabilities.filter((c) => c.scopeType === "community").map((c) => c.name)
  );
  for (const cap of rule2Caps) {
    if (communityCapNames.has(cap)) caps.add(cap);
  }
  debug?.push({
    rule: "community_user_direct_grants",
    matched: directAtTarget.length > 0,
    capsAdded: [...rule2Caps].filter((c) => communityCapNames.has(c)),
    note: `Matched ${directAtTarget.length} direct grant(s) at this community scope.`,
  });

  // ── Rule 6: Operational cascade from ancestors ───────────────
  // Walk the ancestor chain. For each grant at an ancestor whose bundle
  // includes a capability ending in '_descendants', union the matching
  // non-descendants capability into the result for the target.
  //
  // Convention (DEV-854 Decision 3): the suffix '_descendants' marks a
  // cap as cascadable. The base cap (suffix stripped) is what gets
  // conferred at the target. Currently the only cascadable caps are
  // community:view_descendants → community:view and
  // community:admin_descendants → community:admin.
  const cascadedCaps = new Set<CapabilityName>();
  const cascadeSourcesByScope = new Map<GUID, CapabilityName[]>();
  for (const ancestor of inputs.ancestorCommunityGuidsForCommunity) {
    const ancestorGrants = inputs.myGrants.directGrants.filter((g) =>
      isActiveCommunityGrant(g, ancestor) &&
      !isGrantBlockedBy2FA(g.permissionSetGuid)
    );
    for (const g of ancestorGrants) {
      const grantCaps = bundleCaps.get(normGuid(g.permissionSetGuid));
      if (!grantCaps) continue;
      for (const cap of grantCaps) {
        if (cap.endsWith("_descendants")) {
          // DEV-746: slice returns string. The base cap is by construction
          // one of the seeded community caps (the catalog pairs each
          // `*_descendants` cap with its non-cascading sibling) — assert
          // via the runtime `.has` check on communityCapNames.
          const baseCap = cap.slice(0, -"_descendants".length) as CapabilityName;
          if (communityCapNames.has(baseCap)) {
            cascadedCaps.add(baseCap);
            const list = cascadeSourcesByScope.get(ancestor) ?? [];
            list.push(cap);
            cascadeSourcesByScope.set(ancestor, list);
          }
        }
      }
    }
  }

  if (cascadedCaps.size > 0) {
    addAll(caps, cascadedCaps);
    debug?.push({
      rule: "rule_6_operational_cascade",
      matched: true,
      capsAdded: [...cascadedCaps],
      note: `Cascaded ${cascadedCaps.size} cap(s) from ${cascadeSourcesByScope.size} ancestor scope(s).`,
    });
  } else {
    debug?.push({
      rule: "rule_6_operational_cascade",
      matched: false,
      capsAdded: [],
      note: `No _descendants caps found in ${inputs.ancestorCommunityGuidsForCommunity.length} ancestor scope(s).`,
    });
  }

  return { userGuid: inputs.userGuid, communityGuid, capabilities: caps, debug };
}

// ── Helpers ──────────────────────────────────────────────────

function indexBundleCapabilities(
  sets: PermissionSetDto[]
): Map<GUID, Set<CapabilityName>> {
  // DEV-1014. Keys are normalized to upper-case so lookups via
  // `bundleCaps.get(normGuid(g.permissionSetGuid))` are case-insensitive.
  const m = new Map<GUID, Set<CapabilityName>>();
  for (const s of sets) {
    m.set(normGuid(s.permissionSetGuid), new Set(s.capabilities));
  }
  return m;
}

function unionBundleCaps(
  grants: PermissionGrantDto[],
  bundleCaps: Map<GUID, Set<CapabilityName>>
): Set<CapabilityName> {
  const out = new Set<CapabilityName>();
  for (const g of grants) {
    const caps = bundleCaps.get(normGuid(g.permissionSetGuid));
    if (caps) for (const c of caps) out.add(c);
  }
  return out;
}

function addAll<T>(target: Set<T>, source: Set<T>): void {
  for (const x of source) target.add(x);
}
